import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../lib/api';

export enum WorkspaceType {
  SLACK = 'slack',
  PLERION_PLATFORM = 'plerion-platform',
  BROWSER = 'browser',
}

// Request Types
export interface CreateWorkspaceRequest {
  workspaceType: WorkspaceType;
  configuration?: Record<string, unknown>;
  secrets?: Record<string, string>;
  enabled?: boolean;
}

export interface UpdateWorkspaceRequest {
  configuration?: Record<string, unknown>;
  secrets?: Record<string, string>;
  enabled?: boolean;
}

// Response Types
export interface Workspace {
  workspaceType: string;
  enabled: boolean;
  config: Record<string, unknown>;
  secrets?: Record<string, string>;
  organizationId: string;
  createdAt?: string;
  lastUpdated?: string;
}

// Create a new workspace
export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateWorkspaceRequest) => {
      const { data } = await api.post<{ data: Workspace }>(
        '/api/core/pleri/workspaces',
        request,
      );
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['workspaces'] }),
        queryClient.refetchQueries({ queryKey: ['pleriStatus'] }),
      ]);

      return data.data;
    },
  });
};

// Update an existing workspace
export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceType,
      ...request
    }: UpdateWorkspaceRequest & { workspaceType: WorkspaceType }) => {
      const { data } = await api.put<{ data: Workspace }>(
        `/api/core/pleri/workspaces/${workspaceType}`,
        request,
      );
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['workspaces'] }),
        queryClient.refetchQueries({ queryKey: ['pleriStatus'] }),
      ]);

      return data.data;
    },
  });
};

// Disconnect Slack workspace
export const useDisconnectSlack = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.delete('/api/core/pleri/workspaces/slack/disconnect');
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['workspaces'] }),
        queryClient.refetchQueries({ queryKey: ['pleriStatus'] }),
      ]);
    },
  });
};

// Get a single workspace
export const useWorkspace = (workspaceType: WorkspaceType, enabled = true) => {
  return useQuery({
    queryKey: ['workspaces', workspaceType],
    queryFn: async () => {
      const { data } = await api.get<{ data: Workspace }>(
        `/api/core/pleri/workspaces/${workspaceType}`,
      );
      return data.data;
    },
    enabled,
  });
};

// List all workspaces
export const useWorkspaces = (enabled = true) => {
  return useQuery({
    queryKey: ['workspaces'],
    queryFn: async () => {
      const { data } = await api.get<{ data: Workspace[] }>(
        '/api/core/pleri/workspaces',
      );
      return data.data;
    },
    enabled,
  });
};

// Check if browser extension is installed
export const useBrowserExtensionDetected = () => {
  return useQuery({
    queryKey: ['browserExtensionDetected'],
    queryFn: async () => {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        return false;
      }

      // Check if the extension is installed by trying to communicate with it
      return new Promise<boolean>((resolve) => {
        try {
          let resolved = false;

          // Firefox - using MessageChannel response
          const firefoxChannel = new MessageChannel();
          const firefoxMessageId = Math.random().toString(36);

          firefoxChannel.port1.onmessage = (event) => {
            if (event.data.messageId === firefoxMessageId && !resolved) {
              resolved = true;
              resolve(!!event.data.isExtensionInstalled);
            }
          };

          window.postMessage(
            {
              type: 'CHECK_EXTENSION',
              messageId: firefoxMessageId,
              responsePort: firefoxChannel.port2,
            },
            '*',
            [firefoxChannel.port2],
          );

          // Chrome - only if chrome API is available
          if (window.chrome && window.chrome.runtime) {
            // Try to send a message to the extension
            chrome.runtime.sendMessage(
              process.env.CHROME_EXTENSION_ID,
              { type: 'CHECK_EXTENSION' },
              (response) => {
                // If we get a response with isExtensionInstalled: true, the extension is installed
                if (!resolved) {
                  resolved = true;
                  resolve(!!response?.isExtensionInstalled);
                }
              },
            );

            // dev environment
            chrome.runtime.sendMessage(
              // cspell:disable-next-line
              `nonimdcnhbjkcgifpgjbleenlbghnidn`,
              { type: 'CHECK_EXTENSION' },
              (response) => {
                // If we get a response with isExtensionInstalled: true, the extension is installed
                if (!resolved) {
                  resolved = true;
                  resolve(!!response?.isExtensionInstalled);
                }
              },
            );
          }

          // Set a timeout in case the extension doesn't respond
          setTimeout(() => {
            if (!resolved) {
              resolved = true;
              resolve(false);
            }
          }, 1000);
        } catch (error) {
          // If there's an error, the extension is not installed
          console.error('Error checking for browser extension:', error);
          resolve(false);
        }
      });
    },
    // Only run this query in browser environments
    enabled: typeof window !== 'undefined',
  });
};
